import { AccordionMessage, SearchBox } from '@sabavision/dio'
import { isCancel } from 'axios'
import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'
import { GetNotificationResponse, getNotificationsRequest, setReadMessagesRequest } from '../../api/profile'
import { NotificationCategory, NotificationOrder } from '../../api/types'
import { DataContainer, DepositContainer, NotificationContainer } from './styled'

type RouteParams = {
  userId?: string
}

interface SearchOption<T> {
  label: string
  id: T | null
}

const NOTIFICATION_CONFIG = {
  LABELS: {
    [NotificationCategory.FINANCIAL]: 'مالی',
    [NotificationCategory.SYSTEM_ADMINISTRATOR]: 'مدیر سیستم',
    [NotificationCategory.OPERATION]: 'عملکرد',
    [NotificationCategory.MARKETING]: 'بازاریابی',
    [NotificationCategory.EDUCATIONAL]: 'آموزشی'
  },
  COLORS: {
    [NotificationCategory.FINANCIAL]: '#EDFFF7',
    [NotificationCategory.SYSTEM_ADMINISTRATOR]: '#FFF2F0',
    [NotificationCategory.OPERATION]: '#EFF8FF',
    [NotificationCategory.MARKETING]: '#FEEAE3',
    [NotificationCategory.EDUCATIONAL]: '#E9FCDD'
  }
}

const SORT_OPTIONS: SearchOption<NotificationOrder>[] = [
  { label: 'پیش فرض', id: null },
  { label: 'جدیدترین', id: NotificationOrder.NEWEST_FIRST },
  { label: 'قدیمی ترین', id: NotificationOrder.OLDER_FIRST }
]

const FILTER_OPTIONS: SearchOption<NotificationCategory>[] = [
  { label: 'همه', id: null },
  { label: 'مالی', id: NotificationCategory.FINANCIAL },
  { label: 'مدیر سیستم', id: NotificationCategory.SYSTEM_ADMINISTRATOR },
  { label: 'عملکرد', id: NotificationCategory.OPERATION },
  { label: 'بازاریابی', id: NotificationCategory.MARKETING },
  { label: 'آموزشی', id: NotificationCategory.EDUCATIONAL }
]

const SEARCH_DEBOUNCE_MS = 200
const MIN_SEARCH_LENGTH = 4
const PAGE_SIZE = 100

function Messages() {
  const { t } = useTranslation('translation', { keyPrefix: 'sidebar' })
  const { userId } = useParams<RouteParams>()

  const [notifications, setNotifications] = useState<GetNotificationResponse[]>([])
  const [sortBy, setSortBy] = useState<NotificationOrder | null>(null)
  const [filterBy, setFilterBy] = useState<NotificationCategory | null>(null)
  const [searchTerm, setSearchTerm] = useState('')

  const searchDebounceRef = useRef<NodeJS.Timeout>()
  const abortControllerRef = useRef<AbortController>()

  useEffect(() => {
    if (userId) {
      fetchNotifications()
    }

    return () => {
      if (searchDebounceRef.current) {
        clearTimeout(searchDebounceRef.current)
      }
      if (abortControllerRef.current) {
        abortControllerRef.current.abort()
      }
    }
  }, [userId, sortBy, filterBy])

  useEffect(() => {
    fetchNotifications()
  }, [searchTerm])

  const fetchNotifications = () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort()
    }

    abortControllerRef.current = new AbortController()

    getNotificationsRequest(
      userId,
      {
        page_size: PAGE_SIZE,
        ...(sortBy && { order: sortBy }),
        ...(filterBy && { categories: filterBy }),
        ...(searchTerm.length >= MIN_SEARCH_LENGTH && { title: searchTerm })
      },
      setNotifications,
      (error: unknown) => {
        if (!isCancel(error)) {
          console.error('Failed to fetch notifications:', error)
        }
      },
      abortControllerRef.current.signal
    )
  }

  const handleSearchChange = (term: string) => {
    if (searchDebounceRef.current) {
      clearTimeout(searchDebounceRef.current)
    }

    searchDebounceRef.current = setTimeout(() => {
      setSearchTerm(term)
    }, SEARCH_DEBOUNCE_MS)
  }

  const handleReadNotification = (uniqueId: string, isRead: boolean) => {
    if (isRead || !userId) return

    setReadMessagesRequest(
      userId,
      {
        notifications_unique_id: [uniqueId]
      },
      fetchNotifications,
      (error: unknown) => {
        console.error('Failed to mark notification as read:', error)
      }
    )
  }

  return (
    <DataContainer className="align-stretch">
      <DepositContainer>
        <SearchBox
          searchInputPlaceholder={t('search')}
          sort={SORT_OPTIONS.map((option) => ({ id: String(option.id), label: option.label }))}
          filter={FILTER_OPTIONS.map((option) => ({ id: String(option.id), label: option.label }))}
          sortHandleClick={(value) => setSortBy(value.id as NotificationOrder)}
          filterHandleClick={(value) => setFilterBy(value.id as NotificationCategory)}
          searchHandleChange={handleSearchChange}
        />
        <NotificationContainer>
          {notifications.map((notification) => (
            <AccordionMessage
              key={notification.unique_id}
              id={notification.unique_id}
              category={NOTIFICATION_CONFIG.LABELS[notification.category]}
              categoryColor={NOTIFICATION_CONFIG.COLORS[notification.category]}
              title={notification.title}
              message={notification.text}
              date={notification.sent_at}
              seen={!!notification.read_at}
              handleClick={(id) => handleReadNotification(id, !!notification.read_at)}
            />
          ))}
        </NotificationContainer>
      </DepositContainer>
    </DataContainer>
  )
}

export default Messages
